import styled from '@emotion/styled';
import { utSectionStyles, utTokens } from '../sharedStyles';

/**
 * UT Matrix 전기사양 비교 분석 화면 스타일 (LGES_STYLE_GUIDE.md §10~11 기준 —
 * DaStandard/UtMatrixRpt와 동일 톤. 참고한 HTML 툴의 navy/orange 팔레트는 쓰지 않는다).
 */
export const UtComparisonElecRoot = styled.div`
  /* 디자인 토큰·섹션 머리 스타일은 기계 탭과 공유한다 (../sharedStyles.ts) */
  ${utTokens}
  ${utSectionStyles}

  padding: 0 16px 16px;
  color: #1f1f1f;
  font-size: 13px;

  .notice {
    border: 1px solid #8bf0e9; /* Primary-300 */
    background: #ecfcf8; /* Primary-50 */
    color: #135678; /* Primary-800 */
    border-radius: 3px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 12px;
  }
  .error-box {
    border: 1px solid #fda293;
    background: #fef1eb;
    color: #b32542;
    border-radius: 3px;
    padding: 10px 14px;
    font-size: 12px;
    margin-bottom: 12px;
  }
  .loading {
    padding: 24px;
    text-align: center;
    color: #2d9bb2;
    font-size: 12px;
  }
  .empty {
    padding: 40px 20px;
    text-align: center;
    color: #979998;
    font-size: 12px;
    line-height: 1.8;
  }

  .action-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  /* ── 공통 버튼 ── */
  .btn-dark {
    height: 32px;
    padding: 6px 14px;
    border: none;
    border-radius: 2px;
    background: #3c3e3d;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-dark:hover {
    background: #5b5c5b;
  }
  .btn-dark:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  .btn-mode {
    height: 32px;
    padding: 6px 14px;
    border: 1px solid #dde0df;
    border-radius: 2px;
    background: #fff;
    color: #5b5c5b;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-mode:hover {
    background: #f1f4f3;
    border-color: #979998;
  }
  .btn-mode:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  .btn-copy-cell {
    border: none;
    background: transparent;
    padding: 0 2px;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    opacity: 0.65;
    color: #b32542;
  }
  .btn-copy-cell:hover {
    opacity: 1;
  }

  /* ── 패널(①) ── */
  .pane {
    border: 1px solid #dde0df;
    border-radius: 3px;
    background: #fff;
    margin-bottom: 14px;
  }
  .pane-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #dde0df;
    background: #f7f9f8;
  }
  .pane-header h3 {
    font-size: 13px;
    font-weight: 700;
    color: #135678;
  }
  .pane-header .meta {
    font-size: 12px;
    color: #5b5c5b;
  }
  .pane-header .meta em {
    font-style: normal;
    font-weight: 700;
    color: #2d9bb2;
  }
  /*
    ① 비교 대상 표 상자 (CompareTargetSection의 bodyClassName).
    상자 밖 테두리(.pane)를 걷어내면서 아래 여백이 사라져 요약 카드와 붙었다 —
    다음 영역과의 간격을 여기서 준다(.summary-grid의 아래 여백과 같은 14px).
  */
  .pane-body {
    overflow: auto;
    max-height: 260px;
    margin-bottom: 14px;
  }
  .pane-footer {
    display: flex;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid #dde0df;
  }
  .side-tag {
    display: inline-block;
    border-radius: 2px;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid transparent;
  }
  .side-tag.bf {
    background: #fef1eb;
    border-color: #fda293;
    color: #b32542;
  }
  .side-tag.af {
    background: #dcfbea;
    border-color: #56d8aa;
    color: #01463a;
  }
  .ver {
    color: #979998;
    font-size: 11px;
  }

  /* ── 공통 그리드 ── */
  table {
    border-collapse: collapse;
    width: 100%;
  }
  thead th {
    background: #f1f4f3;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    padding: 7px 8px;
    border-right: 1px solid #dde0df;
    border-bottom: 1px solid #dde0df;
    white-space: nowrap;
  }
  thead th:last-child {
    border-right: none;
  }
  tbody td {
    padding: 6px 8px;
    font-size: 12px;
    border-right: 1px solid #f1f4f3;
    border-bottom: 1px solid #f1f4f3;
    white-space: nowrap;
  }
  tbody td:last-child {
    border-right: none;
  }
  tbody tr.selectable {
    cursor: pointer;
  }
  tbody tr.selectable:hover td {
    background: #eeeaf8;
  }
  td.center,
  th.center {
    text-align: center;
  }
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  /* ── 모달(UT Matrix 선택 / 이력 저장 / 차단기 상세) ── */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal {
    width: 640px;
    max-width: calc(100vw - 48px);
    max-height: 82vh;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal.brk-modal {
    width: 1200px;
    max-height: 85vh;
  }
  .modal-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid #dde0df;
    background: #f7f9f8;
  }
  .modal-header h3 {
    font-size: 14px;
    font-weight: 700;
    color: #135678;
  }
  .modal-header .meta {
    font-size: 12px;
    color: #5b5c5b;
  }
  .btn-close {
    margin-left: auto;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid #dde0df;
    border-radius: 2px;
    background: #fff;
    color: #5b5c5b;
    font-size: 14px;
    cursor: pointer;
  }
  .modal-search {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f4f3;
  }
  .modal-search input {
    width: 100%;
    height: 32px;
    padding: 4px 8px;
    border: 1px solid #b9bcbb;
    border-radius: 2px;
    font-size: 13px;
    font-family: inherit;
  }
  .modal-search input:focus {
    outline: none;
    border-color: #3ec2cf;
  }
  .modal-body {
    flex: 1;
    min-height: 120px;
    overflow: auto;
  }
  .modal-note {
    padding: 0 16px 14px;
    font-size: 12px;
    color: #5b5c5b;
    line-height: 1.6;
  }
  .modal-footer {
    padding: 10px 16px;
    border-top: 1px solid #dde0df;
    background: #f7f9f8;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .modal.brk-modal .filter-bar,
  .modal.brk-modal .col-ctrl-bar {
    flex-shrink: 0;
  }
  .modal.brk-modal .modal-body.tbl-wrap {
    max-height: none;
  }

  /* ── ③ 요약 카드 ── */
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }
  .scard {
    background: #fff;
    border: 1px solid #dde0df;
    border-left: 4px solid #dde0df;
    border-radius: 3px;
    padding: 14px 16px;
  }
  .scard.c-total {
    border-left-color: #2d9bb2;
  }
  .scard.c-match {
    border-left-color: #979998;
  }
  .scard.c-chg {
    border-left-color: #ffe56a;
  }
  .scard.c-add {
    border-left-color: #56d8aa;
  }
  .scard.c-del {
    border-left-color: #fda293;
  }
  .scard .n {
    font-size: 26px;
    font-weight: 800;
    color: #135678;
    line-height: 1;
  }
  .scard .l {
    font-size: 11px;
    color: #5b5c5b;
    margin-top: 3px;
    font-weight: 700;
  }
  .scard .sub {
    font-size: 10px;
    color: #979998;
    margin-top: 4px;
    line-height: 1.5;
  }

  /* ── ④ 전압/설비군 패널 + 전체합계 ── */
  .top-panels {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 14px;
    margin-bottom: 14px;
  }
  .panel-box {
    background: #fff;
    border: 1px solid #dde0df;
    border-radius: 3px;
    padding: 14px 16px;
    /*
      grid item 기본값(min-width:auto)이면 안의 내용이 트랙을 밀어낸다(.vcell과 같은 이유).
      예전 표는 .grp-tbl-wrap의 overflow-x:auto가 이를 막아줬는데, CustomGrid로 바꾸면서
      컬럼 합계 너비가 그대로 min-width가 돼 패널이 1fr를 넘어 늘어났다.
    */
    min-width: 0;
  }
  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #135678;
    margin-bottom: 10px;
    padding-bottom: 7px;
    /* 공용 섹션 제목줄(.section-title::after)과 같은 회색 1px 구분선 */
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .volt-tabs {
    display: flex;
    gap: 6px;
  }
  .vtab {
    padding: 3px 12px;
    border-radius: 12px;
    border: 1px solid #dde0df;
    background: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    color: #5b5c5b;
  }
  .vtab.active {
    background: #554596;
    border-color: #554596;
    color: #fff;
  }
  .volt-grid {
    display: grid;
    /*
     * 한 줄에 최대 4개. auto-fit은 폭이 넓으면 5~6개까지 늘어나 셀이 좁아진다.
     * 1fr이 아니라 minmax(0, 1fr)을 쓴다 — 1fr은 minmax(auto, 1fr)이라 트랙이
     * min-content 아래로 못 줄어서, 화면이 좁아지면 그리드가 통째로 넘친다.
     */
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }
  .vcell {
    border-radius: 4px;
    padding: 9px 11px;
    min-width: 0; /* grid item 기본값 min-width:auto면 내용이 셀을 밀어낸다 */
  }
  .vcell .vt {
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 5px;
  }
  /* 셀 폭을 넘지 않도록 — 위 tbody td의 white-space:nowrap을 이 안에서만 되돌린다 */
  .vcell table {
    width: 100%;
  }
  .vcell table td {
    padding: 1px 3px;
    border: none;
    font-size: 11px;
    background: transparent;
    white-space: normal;
    word-break: break-word;
  }
  .vcell table td:last-child {
    text-align: right;
    font-weight: 700;
  }
  .delta {
    font-size: 10px;
    font-weight: 700;
    margin-left: 3px;
  }
  .delta-pos {
    color: #01463a;
  }
  .delta-neg {
    color: #b32542;
  }
  .delta-zero {
    color: #979998;
  }
  .grp-tbl-wrap {
    overflow-x: auto;
    border-radius: 3px;
    border: 1px solid #dde0df;
    max-height: 320px;
    overflow-y: auto;
  }
  table.grp-tbl {
    font-size: 11px;
    min-width: 520px;
  }
  /*
    헤더 색은 위 공통 그리드(thead th — Grey-100 배경 + 기본 텍스트)를 그대로 쓴다.
    예전에는 남색(#135678) 배경 + 흰 글자였는데 표 하나만 튀어 보여 걷어냈다.
    고정(sticky)만 여기서 더한다.
  */
  table.grp-tbl thead th {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  table.grp-tbl tbody td:first-child {
    text-align: left;
    font-weight: 700;
    color: #135678;
  }
  /* 합계 행 — 차단기 표(tr.brk-total)와 같은 톤. 예전 남색 배경 + 흰 글자는 너무 튀었다 */
  tr.grp-total td {
    background: #f1f4f3 !important; /* Grey-100 */
    font-weight: 700;
  }
  tr.grp-new td {
    background: #dcfbea !important;
    color: #01463a !important;
  }
  tr.grp-del td {
    background: #fef1eb !important;
    color: #b32542 !important;
  }
  .tot-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f1f4f3;
    font-size: 12px;
    gap: 8px;
  }
  .tot-row:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 13px;
    padding-top: 7px;
  }
  .tot-label {
    color: #5b5c5b;
    font-weight: 700;
    white-space: nowrap;
  }
  .tot-val {
    color: #135678;
    font-weight: 700;
    text-align: right;
  }

  /* ── ⑤ 변경내역 상세표 ── */
  .table-section {
    background: #fff;
    border: 1px solid #dde0df;
    border-radius: 3px;
    margin-bottom: 14px;
    overflow: hidden;
  }
  .table-header {
    background: #135678;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .table-header h3 {
    color: #fff;
    font-size: 13px;
    font-weight: 700;
  }
  .table-meta {
    color: #bcdfe8;
    font-size: 11px;
  }
  /*
    [26.09.23] 섹션 머리를 공용 SectionBox/SectionHeader로 통일하면서
    자체 접기 버튼(.section-fold-btn)과 .table-section.folded / .brk-section-wrap.folded
    규칙을 걷어냈다. 접기는 이제 SectionBox가 .section-body에 display:none으로 처리한다.
  */
  .filter-bar {
    padding: 8px 14px;
    background: #f7f9f8;
    border-bottom: 1px solid #dde0df;
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
    align-items: center;
  }
  .filter-btn {
    padding: 4px 12px;
    border-radius: 14px;
    border: 1px solid #dde0df;
    background: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    color: #5b5c5b;
  }
  .filter-btn:hover {
    border-color: #2d9bb2;
    color: #2d9bb2;
  }
  .filter-btn.active {
    background: #135678;
    color: #fff;
    border-color: #135678;
  }
  .filter-btn.f-변경.active {
    background: #7a5a00;
    border-color: #7a5a00;
  }
  .filter-btn.f-추가.active {
    background: #01463a;
    border-color: #01463a;
  }
  .filter-btn.f-삭제.active {
    background: #b32542;
    border-color: #b32542;
  }
  .btn-clear-all {
    margin-left: auto;
    padding: 4px 10px;
    border: 1px solid #dde0df;
    border-radius: 3px;
    background: #fff;
    font-size: 11px;
    cursor: pointer;
    color: #5b5c5b;
  }
  .btn-clear-all:hover {
    background: #f1f4f3;
  }
  .col-ctrl-bar {
    padding: 6px 14px;
    background: #fafcfc;
    border-bottom: 1px solid #eef2f1;
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    align-items: center;
  }
  .col-ctrl-bar > span {
    font-size: 11px;
    color: #5b5c5b;
    font-weight: 700;
    margin-right: 2px;
  }
  .col-toggle {
    padding: 2px 9px;
    border-radius: 10px;
    border: 1px solid #dde0df;
    background: #fff;
    font-size: 10px;
    cursor: pointer;
    color: #5b5c5b;
  }
  .col-toggle.hid {
    background: #f1f4f3;
    color: #b9bcbb;
    text-decoration: line-through;
  }
  .tbl-wrap {
    overflow: auto;
    max-height: 480px;
    position: relative;
  }
  table.result {
    table-layout: fixed;
    font-size: 11.5px;
  }
  table.result thead {
    position: sticky;
    top: 0;
    z-index: 3;
  }
  th.col-hdr {
    padding: 0;
    position: relative;
    overflow: visible;
  }
  th.th-loc {
    background: #4569c9;
    color: #fff;
  }
  th.th-elec {
    background: #2e75b6;
    color: #fff;
  }
  .th-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: 5px 4px 2px;
    min-height: 32px;
  }
  .th-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    font-size: 10.5px;
  }
  .th-arrow {
    font-size: 9px;
    cursor: pointer;
    opacity: 0.75;
    padding: 1px 4px;
    border-radius: 3px;
    margin-top: 2px;
  }
  .th-arrow:hover {
    background: rgba(255, 255, 255, 0.25);
    opacity: 1;
  }
  .th-arrow.has-filter {
    color: #ffd700;
    opacity: 1;
  }
  table.result td {
    padding: 5px 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  tr.r-same td {
    background: #fff;
  }
  tr.r-chg td {
    background: #fffbe5;
  }
  tr.r-add td {
    background: #f0faf0;
  }
  tr.r-del td {
    background: #fef0ee;
  }
  .badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .badge-same {
    background: #f1f4f3;
    color: #5b5c5b;
  }
  .badge-chg {
    background: #fffbe5;
    color: #7a5a00;
    border: 1px solid #ffe56a;
  }
  .badge-add {
    background: #dcfbea;
    color: #01463a;
    border: 1px solid #56d8aa;
  }
  .badge-del {
    background: #fef1eb;
    color: #b32542;
    border: 1px solid #fda293;
  }
  td.red {
    color: #b32542;
    font-weight: 700;
  }
  td.conn-chg {
    color: #135678;
    font-weight: 700;
    background: #e3f0ff !important;
  }

  /* ── 칼럼 필터 드롭다운 ── */
  .cfd-backdrop {
    position: fixed;
    inset: 0;
    z-index: 998;
  }
  .cfd {
    position: fixed;
    z-index: 999;
    background: #fff;
    border: 1px solid #dde0df;
    border-radius: 4px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
    min-width: 220px;
    max-width: 280px;
  }
  .cfd-hdr {
    padding: 8px 10px;
    background: #f7f9f8;
    border-bottom: 1px solid #dde0df;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .cfd-hdr b {
    font-size: 12px;
    color: #135678;
  }
  .cfd-hdr button {
    border: none;
    background: none;
    cursor: pointer;
    font-size: 14px;
    color: #979998;
    padding: 0;
  }
  .cfd-sort {
    padding: 6px 10px;
    display: flex;
    gap: 5px;
    border-bottom: 1px solid #f1f4f3;
  }
  .cfd-sort-btn {
    flex: 1;
    padding: 5px 4px;
    border: 1px solid #dde0df;
    border-radius: 3px;
    font-size: 11px;
    cursor: pointer;
    background: #fff;
    text-align: center;
    color: #5b5c5b;
  }
  .cfd-sort-btn.active {
    background: #135678;
    color: #fff;
    border-color: #135678;
  }
  .cfd-sort-btn.cfd-sort-clear {
    flex: 0.6;
    font-size: 10px;
    color: #979998;
  }
  .cfd-search {
    padding: 7px 10px;
    border-bottom: 1px solid #f1f4f3;
  }
  .cfd-search input {
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #dde0df;
    border-radius: 3px;
    font-size: 11px;
    outline: none;
  }
  .cfd-search input:focus {
    border-color: #3ec2cf;
  }
  .cfd-acts {
    padding: 5px 10px;
    display: flex;
    gap: 5px;
    border-bottom: 1px solid #f1f4f3;
  }
  .cfd-acts button {
    flex: 1;
    padding: 4px;
    border: 1px solid #dde0df;
    border-radius: 3px;
    font-size: 10px;
    cursor: pointer;
    background: #fff;
  }
  .cfd-acts button:hover {
    background: #f1f4f3;
  }
  .cfd-list {
    max-height: 180px;
    overflow-y: auto;
    padding: 3px 0;
  }
  .cfd-item {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 4px 10px;
    cursor: pointer;
    font-size: 11px;
  }
  .cfd-item:hover {
    background: #f7f9f8;
  }
  .cfd-foot {
    padding: 7px 10px;
    border-top: 1px solid #f1f4f3;
    display: flex;
    gap: 5px;
  }
  .cfd-ok {
    flex: 1;
    padding: 5px;
    background: #135678;
    color: #fff;
    border: none;
    border-radius: 3px;
    font-size: 11px;
    cursor: pointer;
    font-weight: 700;
  }
  .cfd-cancel {
    padding: 5px 8px;
    background: #fff;
    border: 1px solid #dde0df;
    border-radius: 3px;
    font-size: 11px;
    cursor: pointer;
  }

  /* ── ⑥ 차단기 현황 ── */
  .brk-section-wrap {
    background: #fff;
    border: 1px solid #dde0df;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 14px;
  }
  .brk-hdr {
    background: #135678;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .brk-hdr h3 {
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .brk-hdr-note {
    color: #bcdfe8;
    font-size: 11px;
  }
  .brk-filter-bar {
    padding: 7px 14px;
    background: #f7f9f8;
    border-bottom: 1px solid #dde0df;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }
  .brk-filter-bar > span {
    font-size: 11px;
    font-weight: 700;
    color: #5b5c5b;
  }
  .brk-line-filter-bar {
    padding: 7px 14px;
    background: #fffdf0;
    border-bottom: 1px solid #eae0b0;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }
  .brk-line-filter-bar > span {
    font-size: 11px;
    font-weight: 700;
    color: #7a5a00;
  }
  .grp-btn {
    padding: 3px 10px;
    border-radius: 12px;
    border: 1px solid #dde0df;
    background: #fff;
    font-size: 11px;
    cursor: pointer;
    color: #5b5c5b;
  }
  .grp-btn.active {
    background: #135678;
    color: #fff;
    border-color: #135678;
  }
  .line-btn {
    padding: 3px 10px;
    border-radius: 12px;
    border: 1px solid #d4c870;
    background: #fff;
    font-size: 11px;
    cursor: pointer;
    color: #5b5c5b;
  }
  .line-btn.active {
    background: #7a5a00;
    color: #fff;
    border-color: #7a5a00;
  }
  .brk-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    padding: 14px;
    gap: 12px;
  }
  .brk-card {
    border-radius: 3px;
    overflow: hidden;
    border: 1px solid #dde0df;
  }
  .brk-card-hdr {
    padding: 7px 13px;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
  }
  .brk-card-hdr.bh1 {
    background: #3b5db7;
  }
  .brk-card-hdr.bh2 {
    background: #554596;
  }
  .brk-card-hdr.bh3 {
    background: #01463a;
  }
  .brk-card-hdr.bh4 {
    background: #7a5a00;
  }
  .brk-card-hdr.bh5 {
    background: #b32542;
  }
  .brk-card-hdr.bh6 {
    background: #135678;
  }
  .brk-card-hdr.bh-tot {
    background: #3c3e3d;
  }
  table.brk-tbl {
    font-size: 11.5px;
  }
  table.brk-tbl th {
    background: #f7f9f8;
    padding: 5px 9px;
    font-weight: 700;
    color: #5b5c5b;
    font-size: 11px;
    text-align: center;
  }
  table.brk-tbl td {
    padding: 5px 9px;
    text-align: center;
    border-bottom: none;
    border-right: none;
  }
  tr.brk-total td {
    background: #f1f4f3;
    font-weight: 700;
  }
  .brk-click {
    cursor: pointer;
    color: #2d9bb2;
    text-decoration: underline;
    font-weight: 700;
  }
  .brk-click:hover {
    color: #135678;
  }
  .diff-p {
    color: #01463a;
    font-weight: 700;
  }
  .diff-n {
    color: #b32542;
    font-weight: 700;
  }

  /* ── 전체 종합의견 패널 (이력 로드 시) ── */
  .opinion-panel {
    border: 1px solid #dde0df;
    border-radius: 3px;
    background: #fff;
    margin: 20px 0 24px;
    padding: 16px;
    font-size: 13px;
  }
  .panel-title-lg {
    font-size: 14px;
    font-weight: 700;
    color: #135678;
    margin-bottom: 10px;
  }
  .rpt-tag {
    font-size: 11px;
    font-weight: 400;
    color: #5b5c5b;
    margin-left: 8px;
  }
  /* AI 분석 항목/종합의견 소제목 — 항목 본문은 공용 AnalysisOpinionSection이
     인라인 스타일로 그리므로(기계 화면과 공유) 여기서는 제목 스타일만 둔다. */
  .ai-block-title {
    font-size: 13px;
    font-weight: 700;
    margin: 0 0 8px;
  }
  .opin-input {
    width: 100%;
    min-height: 80px;
    padding: 8px 10px;
    border: 1px solid #b9bcbb;
    border-radius: 2px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    margin-bottom: 8px;
  }
  .opin-input:focus {
    outline: none;
    border-color: #3ec2cf;
  }
  .copy-msg {
    font-size: 12px;
    color: #01a982;
    margin-left: 8px;
    font-weight: 700;
  }
`;
